import React, { useCallback } from 'react';
import { Building2 } from 'lucide-react';
import T from 'Components/Helpers/translate';
import AgentApprovalsResource from 'Resources/AgentApprovals';
import ProposalCardShell, { type StatusPill } from 'App/shadcn/pages/AIWorkspace/components/ProposalCardShell';
import { executionFailureMessage } from './approvalAdapters';
import { openCrmEntity } from './openCrmEntity';
import type { ProspectingAddConfig } from './types';

type Props = {
	config: ProspectingAddConfig;
	onUpdate?: (id: string, updates: Partial<ProspectingAddConfig>) => void;
	defaultExpanded?: boolean;
};

/**
 * A company, or contacts, the Prospecting Agent wants to add to the CRM (WEB-11807).
 *
 * Nothing here writes to the CRM. Approving posts to the approval row, and the
 * server runs the same prospecting import the agent would have run, so the
 * company still arrives as the full register record. That is also why there is
 * nothing to edit: the fields come from the register, not from the agent.
 */
const ProspectingAddCard = ({ config, onUpdate, defaultExpanded }: Props) => {
	const { status, contacts, companyName, orgNumber, clientId } = config;
	const isCompany = config.action === 'prospecting_add_company';

	const handleApprove = useCallback(async () => {
		if (!onUpdate || !config.approvalId) return;
		onUpdate(config.id, { status: 'executing' });
		try {
			const { approval } = await AgentApprovalsResource.approve(config.approvalId);
			const failure = executionFailureMessage(
				approval.resultPayload as Record<string, unknown> | null | undefined
			);
			if (failure !== null) {
				onUpdate(config.id, { status: 'failed', resultMessage: failure || T('default.failed') });
				return;
			}
			onUpdate(config.id, { status: 'completed', clientId: approval.resultEntityId ?? undefined });
		} catch (err) {
			onUpdate(config.id, {
				status: 'failed',
				resultMessage: err instanceof Error ? err.message : T('default.failed')
			});
		}
	}, [config.approvalId, config.id, onUpdate]);

	const handleReject = useCallback(async () => {
		if (!onUpdate || !config.approvalId) return;
		try {
			await AgentApprovalsResource.reject(config.approvalId, 'cancelled_in_chat');
			onUpdate(config.id, { status: 'cancelled' });
		} catch (err) {
			onUpdate(config.id, {
				status: 'failed',
				resultMessage: err instanceof Error ? err.message : T('default.failed')
			});
		}
	}, [config.approvalId, config.id, onUpdate]);

	const statusPill: StatusPill | null =
		status === 'executing'
			? { label: T('assistant.prospectingAdd.adding'), tone: 'saving' }
			: status === 'completed'
			? { label: T('assistant.prospectingAdd.added'), tone: 'ok' }
			: status === 'cancelled'
			? { label: T('assistant.cancelled'), tone: 'muted' }
			: status === 'failed'
			? { label: T('default.failed'), tone: 'error' }
			: null;

	const contactCount = T(
		contacts.length === 1 ? 'assistant.prospectingAdd.contactCountOne' : 'assistant.prospectingAdd.contactCount',
		{ count: contacts.length }
	);
	const chips = [...(orgNumber ? [orgNumber] : []), ...(contacts.length ? [contactCount] : [])];

	const body = (
		<div className="ws-prop-card-body-text">
			{chips.length ? (
				<div className="ws-prop-card-facts">
					<strong>{chips.join(' · ')}</strong>
				</div>
			) : null}
			{status === 'pending' ? <div>{T('assistant.prospectingAdd.explainer')}</div> : null}
			{contacts.length ? (
				<div>{contacts.map(c => (c.role ? `${c.name} (${c.role})` : c.name)).join(', ')}</div>
			) : null}
			{status === 'completed' && clientId ? (
				<div>
					<a
						href="#"
						onClick={e => {
							e.preventDefault();
							openCrmEntity('client', clientId);
						}}
					>
						{T('assistant.prospectingAdd.openCompany')}
					</a>
				</div>
			) : null}
			{config.resultMessage ? <div>{config.resultMessage}</div> : null}
		</div>
	);

	return (
		<ProposalCardShell
			icon={Building2}
			title={
				isCompany
					? T('assistant.prospectingAdd.titleCompany', { name: companyName ?? '' })
					: T('assistant.prospectingAdd.titleContacts')
			}
			chips={chips}
			statusPill={statusPill}
			body={body}
			busy={status === 'executing'}
			resolved={status === 'completed' || status === 'cancelled' || status === 'failed'}
			approveLabel={T('assistant.prospectingAdd.approve')}
			onApprove={handleApprove}
			onReject={handleReject}
			defaultExpanded={defaultExpanded}
		/>
	);
};

export default ProspectingAddCard;
